import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, Button } from '@/components/ui';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, type DataTableAction, type DataTableColumn } from '@/components/ui/DataTable';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { customReportsApi } from '@/services/customReports.service';
import { aiReportsApi } from '@/services/aiReports.service';
import { standardReportsService } from '@/services/standardReports.service';
import { getErrorMessage } from '@/services/apiClient';
import { useAuth } from '@/hooks/useAuth';
import type { BadgeVariant } from '@/types/common.types';
import { useMyReports, type MyReportRow } from '@/hooks/useMyReports';
import {
  MY_REPORTS_FILTER_CONFIGS,
  REPORT_KIND_OPTIONS,
  REPORT_OWNERSHIP_OPTIONS,
  REPORT_SCHEDULE_OPTIONS,
  REPORT_STATUS_OPTIONS,
  useLocalDashboardFilters,
  selectValues,
} from '@/pages/dashboard/dashboardFilters';

/** Matches the page size other list pages use. */
const PER_PAGE = 50;

/**
 * Every report the user can see, in one list.
 *
 * WHAT THIS REPLACES. Three pages used to divide the same reports between them —
 * My Reports (results only), Scheduled Reports (definitions with a schedule) and
 * History (runs) — plus an admin-only Manage page listing definitions again.
 * Which page a given report appeared on depended on facts about the backend that
 * were never on screen, so the honest answer to "where is my report?" was "try
 * all four". They are now one table.
 *
 * A ROW IS A REPORT, NOT A RUN. A weekly report is one row however many times it
 * has fired, and its runs open underneath it — which is what gives pause/resume
 * somewhere to live.
 *
 * TYPE IS A COLUMN, NOT A TAB. It changes what a row can do, not what it is —
 * grouping by it would rebuild the split this page exists to remove.
 *
 * NAME IS NOT ENOUGH TO IDENTIFY A ROW. Practices accumulate many reports called
 * "Patients" or "AllPayments", so the Report and Filters columns are not
 * decoration: they are the only thing that tells two rows apart. A first version
 * shipped without them and was unusable for exactly that reason.
 */
export default function MyReportsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userLevel = user?.userLevel ?? 0;

  const [actionError, setActionError] = useState<string | null>(null);
  const [deleteItem, setDeleteItem] = useState<MyReportRow | null>(null);

  const { rows: allRows, loading, invalidate } = useMyReports();

  // ── Filters ────────────────────────────────────────────────────────────
  // Applied client-side because the list is already assembled client-side from
  // several endpoints; there is no single query to push them into.
  const filters = useLocalDashboardFilters(MY_REPORTS_FILTER_CONFIGS, 'my-reports');

  const kindSel = selectValues(filters.values, 'reportKind');
  const scheduleSel = selectValues(filters.values, 'schedule');
  const statusSel = selectValues(filters.values, 'status');
  const ownershipSel = selectValues(filters.values, 'ownership');
  const search = String(filters.values['search'] ?? '').trim().toLowerCase();

  const rows = useMemo(() => {
    return allRows.filter((r) => {
      if (kindSel.length > 0 && !kindSel.includes(r.kind)) return false;

      if (scheduleSel.length > 0) {
        const key = r.recurring ? 'recurring' : 'oneoff';
        if (!scheduleSel.includes(key)) return false;
      }

      if (statusSel.length > 0) {
        const key = r.active ? 'active' : 'paused';
        if (!statusSel.includes(key)) return false;
      }

      if (ownershipSel.length > 0) {
        const key = r.sharedWithMe ? 'shared' : 'mine';
        if (!ownershipSel.includes(key)) return false;
      }

      // Filters are searchable too — on a list where many reports share a name,
      // the filter text is often the only thing a user remembers.
      const haystack = `${r.title} ${r.reportName} ${r.subReport ?? ''} ${r.filterSummary ?? ''}`;
      if (search && !haystack.toLowerCase().includes(search)) return false;

      return true;
    });
  }, [allRows, kindSel, scheduleSel, statusSel, ownershipSel, search]);

  // Paginated client-side, since the list is assembled here from several
  // sources rather than fetched as one page. Rendering every row at once is
  // what the first version did.
  const [page, setPage] = useState(1);
  const pageRows = useMemo(
    () => rows.slice((page - 1) * PER_PAGE, page * PER_PAGE),
    [rows, page],
  );

  // Any filter change invalidates the current page number.
  useEffect(() => {
    setPage(1);
  }, [kindSel.length, scheduleSel.length, statusSel.length, ownershipSel.length, search]);

  // ── Actions ────────────────────────────────────────────────────────────
  // Each dispatches to whichever API owns the row. The kinds have genuinely
  // different backends; the point of the merge is that the USER does not have to
  // know that, not that the difference stops existing.
  const onError = (err: unknown) => setActionError(getErrorMessage(err, 'Action failed'));
  const onDone = () => {
    setActionError(null);
    invalidate();
  };

  const runNow = useMutation({
    mutationFn: async (row: MyReportRow): Promise<void> => {
      if (row.kind === 'standard') {
        await standardReportsService.runOrderNow(row.id);
        return;
      }
      if (row.kind === 'ai') {
        await aiReportsApi.schedules.runNow(row.id);
        return;
      }
      // Scheduled custom reports are fired by the legacy cron, not on demand —
      // `canRunNow` is false for them, so the action never reaches here. Guarding
      // rather than calling the on-demand endpoint, which addresses a different
      // table and would silently act on the wrong record.
      throw new Error('This report runs on its schedule and cannot be started manually.');
    },
    onSuccess: onDone,
    onError,
  });

  const pause = useMutation({
    mutationFn: async (row: MyReportRow): Promise<void> => {
      if (row.kind === 'standard') {
        await standardReportsService.pauseOrder(row.id);
        return;
      }
      if (row.kind === 'ai') {
        await aiReportsApi.schedules.pause(row.id);
        return;
      }
      await customReportsApi.scheduled.pause(row.id);
    },
    onSuccess: onDone,
    onError,
  });

  const resume = useMutation({
    mutationFn: async (row: MyReportRow): Promise<void> => {
      if (row.kind === 'standard') {
        await standardReportsService.resumeOrder(row.id);
        return;
      }
      if (row.kind === 'ai') {
        await aiReportsApi.schedules.resume(row.id);
        return;
      }
      await customReportsApi.scheduled.resume(row.id);
    },
    onSuccess: onDone,
    onError,
  });

  const remove = useMutation({
    mutationFn: async (row: MyReportRow): Promise<void> => {
      if (row.kind === 'standard') {
        await standardReportsService.deleteOrder(row.id);
        return;
      }
      if (row.kind === 'ai') {
        await aiReportsApi.schedules.remove(row.id);
        return;
      }
      await customReportsApi.scheduled.remove(row.id);
    },
    onSuccess: () => {
      setDeleteItem(null);
      onDone();
    },
    onError: (e) => {
      setDeleteItem(null);
      onError(e);
    },
  });

  // The row carries its own destination. Assembling it here from `kind` and
  // `id` assumed `id` meant the same thing for every row of a kind, and it does
  // not — see `openPath` in useMyReports.
  const openReport = (row: MyReportRow) => navigate(row.openPath);

  // ── Columns ────────────────────────────────────────────────────────────
  const columns: DataTableColumn<MyReportRow>[] = [
    {
      key: 'title',
      header: 'Name',
      render: (row) => (
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-slate-900">{row.title}</span>
            {row.sharedWithMe && <Badge variant="info">Shared</Badge>}
          </div>
        </div>
      ),
    },
    {
      // The SUBTYPE, restored. This column existed on the legacy list and on the
      // page this replaced, and dropping it left rows identified only by a name
      // that is the same on hundreds of them.
      key: 'reportName',
      header: 'Report',
      width: 'w-[15%]',
      truncate: true,
      render: (row) => (
        <div className="min-w-0">
          <div className="truncate text-slate-700">{row.reportName}</div>
          {row.subReport && <div className="truncate text-xs text-slate-500">{row.subReport}</div>}
        </div>
      ),
    },
    {
      // What it ran with — the only thing that tells two same-named reports
      // apart. Same width and truncation the legacy column used.
      key: 'filterSummary',
      header: 'Filters',
      width: 'w-[20%]',
      truncate: true,
      render: (row) =>
        row.filterSummary ? (
          <span className="text-slate-600" title={row.filterSummary}>
            {row.filterSummary}
          </span>
        ) : (
          <span className="text-slate-400">No filters</span>
        ),
    },
    {
      key: 'kind',
      header: 'Type',
      width: 'w-[8%]',
      render: (row) => <Badge variant={kindBadge(row.kind)}>{kindLabel(row.kind)}</Badge>,
    },
    {
      key: 'schedule',
      header: 'Schedule',
      width: 'w-[12%]',
      render: (row) => {
        if (!row.recurring) return <span className="text-slate-400">One-off</span>;

        return (
          <div>
            <div className="text-slate-700">{row.cadence ?? 'Recurring'}</div>
            {row.nextRun && <div className="text-xs text-slate-500">next {fmt(row.nextRun)}</div>}
          </div>
        );
      },
    },
    {
      key: 'lastRun',
      header: 'Last run',
      width: 'w-[12%]',
      render: (row) =>
        row.lastRun ? (
          <div>
            <div className="text-slate-700">{fmt(row.lastRun)}</div>
            {row.runCount !== null && row.runCount > 1 && (
              <div className="text-xs text-slate-500">{row.runCount} runs</div>
            )}
          </div>
        ) : (
          // Never run is a real state, not a blank. It is what the Manage page's
          // greyed-out definitions become.
          <span className="text-slate-400">Never run</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      width: 'w-[9%]',
      render: (row) => {
        if (!row.active) return <Badge variant="neutral">Paused</Badge>;
        // Before the run states: a waiting report is `active` too, so it would
        // otherwise fall through to "Active" and read as though it had run.
        if (row.pending === 'scheduled') return <Badge variant="info">Scheduled</Badge>;
        if (row.pending === 'preparing') return <Badge variant="warning">Preparing</Badge>;
        if (row.lastRunStatus) return <Badge variant={statusBadge(row.lastRunStatus)}>{row.lastRunStatus}</Badge>;

        return <Badge variant="success">Active</Badge>;
      },
    },
  ];

  const actions: DataTableAction<MyReportRow>[] = [
    {
      label: 'Open',
      onClick: openReport,
    },
    {
      label: 'Run now',
      onClick: (row) => runNow.mutate(row),
      hidden: (row) => !row.canRunNow,
    },
    {
      label: 'Pause',
      onClick: (row) => pause.mutate(row),
      hidden: (row) => !row.canManage || !row.recurring || !row.active,
    },
    {
      label: 'Resume',
      onClick: (row) => resume.mutate(row),
      hidden: (row) => !row.canManage || !row.recurring || row.active,
    },
    {
      label: 'Edit',
      onClick: (row) => {
        if (row.editPath) navigate(row.editPath);
      },
      hidden: (row) => row.editPath === null || !row.canManage,
    },
    {
      label: 'Delete',
      variant: 'danger',
      onClick: (row) => setDeleteItem(row),
      // Matches the legacy rule this page inherited: deletion is a level-3 act.
      hidden: (row) => !row.canManage || userLevel <= 2,
    },
  ];

  return (
    <ListPageLayout
      filterSidebar={
        <FilterSidebar
          filters={MY_REPORTS_FILTER_CONFIGS}
          values={filters.values}
          options={{
            reportKind: REPORT_KIND_OPTIONS,
            schedule: REPORT_SCHEDULE_OPTIONS,
            status: REPORT_STATUS_OPTIONS,
            ownership: REPORT_OWNERSHIP_OPTIONS,
          }}
          onChange={filters.setFilter}
          onClear={filters.clearFilter}
          searchCommitMode="blur-or-enter"
          // Open on arrival, unlike most list pages. Finding one report among
          // hundreds that share a name is the whole job here, so the search box
          // has to be on screen rather than behind the collapse. The choice is
          // still persisted per user under `storageKey`.
          defaultCollapsed={false}
          showAIAssistant={false}
          storageKey="my-reports"
        />
      }
    >
      <div className="flex h-full min-h-0 flex-col overflow-x-hidden max-md:h-auto">
        <div className="shrink-0 p-6">
          <PageHeader
            variant="compact"
            title="My Reports"
            actions={
              <Button
                size="sm"
                leftIcon={<Plus className="h-3.5 w-3.5" />}
                onClick={() => navigate('/reports')}
              >
                New Report
              </Button>
            }
          />

          {actionError && <Alert variant="error">{actionError}</Alert>}
        </div>

        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
          <DataTable<MyReportRow>
            data={pageRows}
            columns={columns}
            keyField="_key"
            rowActions={actions}
            onRowClick={openReport}
            loading={loading}
            tableFixed
            page={page}
            pageSize={PER_PAGE}
            total={rows.length}
            onPageChange={setPage}
            className="[&>div]:overflow-x-hidden"
            emptyTitle="No reports yet"
            emptyDescription="Run a pre-built report or build your own from New Report."
          />
        </div>
      </div>

      {deleteItem && (
        <ConfirmDialog
          isOpen
          title="Delete this report?"
          message={
            deleteItem.recurring
              ? `"${deleteItem.title}" and its schedule will be removed. Runs already delivered are unaffected.`
              : `"${deleteItem.title}" will be removed.`
          }
          confirmLabel="Delete"
          variant="danger"
          onConfirm={() => remove.mutate(deleteItem)}
          onClose={() => setDeleteItem(null)}
        />
      )}
    </ListPageLayout>
  );
}

function kindLabel(kind: MyReportRow['kind']): string {
  if (kind === 'standard') return 'Standard';
  if (kind === 'ai') return 'AI';

  return 'Custom';
}

function kindBadge(kind: MyReportRow['kind']): BadgeVariant {
  if (kind === 'standard') return 'info';
  if (kind === 'ai') return 'purple';

  return 'neutral';
}

function statusBadge(status: string): BadgeVariant {
  if (status === 'ready') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'too_large' || status === 'empty') return 'warning';

  return 'neutral';
}

/** Dates arrive in several shapes across the three backends; show the day. */
function fmt(value: string): string {
  const d = new Date(value);

  return Number.isNaN(d.getTime()) ? value.slice(0, 10) : d.toLocaleDateString();
}
